import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useProject } from "@/hooks/useData";
import { imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LeadDialog } from "@/components/LeadDialog";
import { Eyebrow } from "@/components/Reveal";
import { ArrowLeft } from "lucide-react";

const Meta = ({ label, value }) =>
  value ? (
    <div>
      <p className="label-eyebrow text-[10px] text-[#9E8773]">{label}</p>
      <p className="text-[#2C2A29] font-light mt-1">{value}</p>
    </div>
  ) : null;

export default function ProjectDetail() {
  const { id } = useParams();
  const { data: project, isLoading } = useProject(id);

  if (isLoading) return <div className="pt-44 text-center text-[#5E5A54]">Loading…</div>;
  if (!project) return <div className="pt-44 text-center text-[#5E5A54]" data-testid="project-not-found">Project not found.</div>;

  return (
    <div className="pt-28 sm:pt-32" data-testid="project-detail-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Link to="/projects" className="inline-flex items-center gap-2 label-eyebrow text-[11px] text-[#5E5A54] hover:text-[#2C2A29] transition-colors mb-8" data-testid="back-to-projects">
          <ArrowLeft size={15} /> All projects
        </Link>
        <Eyebrow>{project.country} · {project.project_type}</Eyebrow>
        <h1 className="font-display text-5xl sm:text-6xl font-medium text-[#2C2A29] mt-3 max-w-3xl" data-testid="project-title">{project.title}</h1>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="img-frame aspect-[16/9] mt-10 max-w-7xl mx-auto px-5 sm:px-8">
        <img src={imgUrl(project.cover_image)} alt={project.title} className="w-full h-full object-cover" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="text-xl text-[#5E5A54] font-light leading-relaxed">{project.description}</p>
            {project.scope?.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2">
                {project.scope.map((s) => (
                  <span key={s} className="label-eyebrow text-[10px] px-4 py-2 border border-[#ECEAE4] text-[#5E5A54]">{s}</span>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-4 lg:col-start-9 grid grid-cols-2 gap-6 content-start">
            <Meta label="Client" value={project.client_type} />
            <Meta label="Year" value={project.year} />
            <Meta label="Location" value={project.country} />
            <Meta label="Type" value={project.project_type} />
          </div>
        </div>

        {project.gallery?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-16">
            {project.gallery.map((g, i) => (
              <div key={i} className="img-frame aspect-[4/3]">
                <img src={imgUrl(g)} alt={`${project.title} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}

        <div className="mt-20 text-center bg-[#F6F5F2] py-16 px-6">
          <h3 className="font-display text-3xl sm:text-4xl font-medium text-[#2C2A29]">Planning something similar?</h3>
          <p className="text-[#5E5A54] font-light mt-3 max-w-lg mx-auto">Share your project and we'll build a tailored sourcing plan.</p>
          <div className="mt-8">
            <LeadDialog trigger={<Button className="rounded-none bg-[#2C2A29] hover:bg-[#4A4641] text-[#FFFFFF] h-12 px-8 label-eyebrow text-[10px]" data-testid="project-cta-button">Start Your Project</Button>} />
          </div>
        </div>
      </div>
    </div>
  );
}
